import React, { forwardRef, Ref, useState, useCallback } from 'react';
import classnames from 'classnames';
import { Input as AntdInput, InputRef } from 'antd';
import type { TextAreaProps } from 'antd/es/input';

const PREFIX = 'music';

export interface MusicTextAreaProps extends TextAreaProps {
  /** 内部 label，显示在字数统计对面（左上角） */
  label?: React.ReactNode;
}

// 每行约 1.5em（line-height），加上 padding
const LINE_HEIGHT_EM = 1.5;

function _TextArea(props: MusicTextAreaProps, ref: Ref<InputRef>) {
  const { className, showCount, maxLength, value, defaultValue, onChange, label, rows = 3, style, ...rstProps } = props;

  const [internalLen, setInternalLen] = useState(() => {
    const v = value ?? defaultValue ?? '';
    return typeof v === 'string' ? v.length : 0;
  });

  const currentLen = value !== undefined ? (typeof value === 'string' ? value.length : 0) : internalLen;
  const showCountOutside = !!showCount;
  const showHeaderRow = !!label || showCountOutside;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (value === undefined) {
        setInternalLen(e.target.value.length);
      }
      onChange?.(e);
    },
    [onChange, value]
  );

  const textareaStyle = {
    ...style,
    minHeight: `${Number(rows) * LINE_HEIGHT_EM}em`,
  };

  const textarea = (
    <AntdInput.TextArea
      className={classnames(
        `${PREFIX}-input`,
        `${PREFIX}-input-textarea`,
        className
      )}
      ref={ref}
      maxLength={maxLength}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      rows={rows}
      style={textareaStyle}
      {...rstProps}
    />
  );

  if (showHeaderRow) {
    return (
      <div className={`${PREFIX}-input-textarea-wrapper`}>
        <div className={`${PREFIX}-input-textarea-header`}>
          {label ? <span className={`${PREFIX}-input-textarea-label`}>{label}</span> : <span />}
          {showCountOutside ? (
            <span className={`${PREFIX}-input-textarea-count`}>
              {currentLen}
              {maxLength != null && ` / ${maxLength}`}
            </span>
          ) : (
            <span />
          )}
        </div>
        {textarea}
      </div>
    );
  }

  return textarea;
}

const TextArea = forwardRef(_TextArea);

export default TextArea;
